<template>
    <!-- Backdrop over the uncovered half — tablet portrait only (phone drawer is full-width). Click to close. -->
    <div
        @click="mobileFiltersMenuStore.closeMenu()"
        class="fixed inset-0 z-[59] hidden bg-black/40 transition-opacity duration-200 md:block lg:hidden"
        :class="mobileFiltersMenuStore.isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'"></div>
    <div
        class="fixed top-0 left-0 z-[60] flex h-full w-full flex-col bg-white transition-transform duration-200 ease-in-out md:w-1/2 lg:w-full"
        :class="mobileFiltersMenuStore.isOpen ? 'translate-x-0' : '-translate-x-full'">
        <BlockProductListingFiltersMobileTitle />
        <div class="flex-1 overflow-y-auto px-7 pb-3">
            <BlockProductListingFiltersMobileSorting :sorts="sorts" :currentSort="currentSort" />

            <BlockProductListingFiltersCurrentFilters
                @invokeFilter="filterProducts"
                :isOpen="true"
                :currentFilters="currentFilters"
                :filters="orderedFilters" />

            <BlockProductListingFiltersSearchCategoryTree
                v-if="searchCategoryTree"
                :tree="searchCategoryTree"
                :total="total"
                :is-open="false" />
            <BlockProductListingFiltersSearchCategoryTree
                v-else-if="manufacturerCategoryTree?.items?.length"
                :tree="manufacturerCategoryTree"
                :seo-url-map="manufacturerSeoUrlMap"
                :is-manufacturer-page="true"
                :total="total"
                :is-open="false" />
            <BlockProductListingFiltersCategoryTree
                v-else-if="showShopwareTree"
                :categories="categorySiblings"
                :current-category-id="currentCategoryId"
                :total="total"
                :is-open="false" />

            <template v-for="(filter, index) in orderedFilters" :key="getFilterKey(filter)">
                <BlockProductListingFiltersManufacturer
                    v-if="isManufacturerFilter(filter)"
                    @invokeFilter="(id, idx) => filterProducts(id, idx)"
                    :isOpen="false"
                    :currentFilters="currentFilters"
                    :index="index"
                    :code="filter.code"
                    :label="'Merken'"
                    :total="total"
                    :entities="filter?.entities"
                    :options="filter?.options" />
                <BlockProductListingFiltersPrice
                    v-else-if="isPriceFilter(filter)"
                    @invokeFilter="filterProducts"
                    :filter="filter"
                    :total="total"
                    :currentFilters="currentFilters"
                    :index="index"
                    :min="getPriceMinFromOptions(filter)"
                    :max="getPriceMaxFromOptions(filter)"
                    :label="filter.label"
                    :isOpen="false" />
                <BlockProductListingFiltersFilter
                    v-else-if="filter.options"
                    @invokeFilter="filterProducts"
                    :filter="filter"
                    :total="total"
                    :currentFilters="currentFilters"
                    :index="index"
                    :code="filter.code"
                    :label="filter.label"
                    :options="filter.options"
                    :isOpen="false" />
            </template>
        </div>
        <div class="px-6 pb-5">
            <button @click="mobileFiltersMenuStore.closeMenu()" class="btn !w-full bg-black text-white">
                {{ $snippets['headless.plp.view'] }} {{ total }} {{ $snippets['headless.plp.items'] }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {
        isManufacturerFilter,
        isPriceFilter,
        getFilterKey,
        getPriceMinFromOptions,
        getPriceMaxFromOptions,
    } from '~/utils/plp';

    const props = defineProps({
        sorts: Object,
        currentFilters: Object,
        currentSort: String,
        orderedFilters: Object,
        total: Number,
        categorySiblings: Array,
        currentCategoryId: String,
        searchCategoryTree: Object,
        isManufacturerPage: Boolean,
        manufacturerCategoryTree: Object,
        manufacturerSeoUrlMap: Object,
        treeSettled: Boolean,
    });

    const mobileFiltersMenuStore = useMobileFiltersMenuStore();

    // Same rule as the desktop sidebar: the Shopware sibling tree is a fallback only, shown
    // once Tweakwise has settled and only off manufacturer pages.
    const showShopwareTree = computed(
        () => !props.isManufacturerPage && props.treeSettled && ((props.categorySiblings as any[])?.length ?? 0) > 0,
    );

    const emit = defineEmits(['invokeFilter']);

    const filterProducts = (id: string, index: Number) => {
        emit('invokeFilter', id, index);
    };
</script>
